import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import VoiceRecorder from '../components/VoiceRecorder'

const VoiceRecord: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  
  const teamId = searchParams.get('team')
  const userId = searchParams.get('user')
  
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (!teamId || !userId) {
      setError('Missing team or user information. Please try again from Slack.')
    }
  }, [teamId, userId])

  const handleStandupSubmitted = (data: any) => {
    console.log('Voice standup submitted:', data)
    setSubmitted(true)
    
    // Auto-close after a few seconds or show success message
    setTimeout(() => {
      window.close() // Try to close the window if opened as popup
    }, 3000)
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="text-red-600 text-xl mb-4">⚠️ Error</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Go Home
          </button>
        </div>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="text-green-600 text-xl mb-4">✅ Success!</div>
          <p className="text-gray-600 mb-4">
            Your voice standup has been processed and saved successfully!
          </p>
          <p className="text-sm text-gray-500">
            This window will close automatically, or you can close it manually.
          </p>
          <button 
            onClick={() => window.close()}
            className="mt-4 bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            Close Window
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <h1 className="text-2xl font-bold text-gray-900">🎤 Voice Standup Recording</h1>
            <p className="text-gray-600">Record your daily standup and we'll transcribe it for you</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {teamId && userId && (
          <VoiceRecorder
            onStandupSubmitted={handleStandupSubmitted}
            userId={userId}
            teamId={teamId}
            supabaseUrl={import.meta.env.VITE_SUPABASE_URL as string}
          />
        )}
        
        {/* Instructions */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-4">How to record your standup:</h3>
          <div className="space-y-3 text-blue-800">
            <div className="flex items-start">
              <span className="font-bold mr-2">1.</span>
              <span>Click "Start Recording" and speak naturally about your standup</span>
            </div>
            <div className="flex items-start">
              <span className="font-bold mr-2">2.</span>
              <span>Mention what you did yesterday, what you plan to do today, and any blockers</span>
            </div>
            <div className="flex items-start">
              <span className="font-bold mr-2">3.</span>
              <span>You can pause and resume recording if needed</span>
            </div>
            <div className="flex items-start">
              <span className="font-bold mr-2">4.</span>
              <span>Review your recording by playing it back</span>
            </div>
            <div className="flex items-start">
              <span className="font-bold mr-2">5.</span>
              <span>Click "Submit Standup" to process and save your standup</span>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-blue-100 rounded">
            <p className="text-sm text-blue-800">
              <strong>💡 Pro tip:</strong> Speak clearly and naturally. Our AI is designed to understand 
              conversational speech and will automatically extract the key information from your recording.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VoiceRecord 